import React, { useState, useEffect } from "react";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Button from "@mui/material/Button";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";
import Select from "@mui/material/Select";
import Checkbox from "@mui/material/Checkbox";
import Modal from "@mui/material/Modal";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import axios from "axios";
import Sidebar from "../../../components/sidebar/Sidebar";
import Navbar from "../../../components/navbar/Navbar";
import Loader from "../../../components/Loader/Loader";
import Swal from "sweetalert2";
import "../../../components/common/css/campaignview.scss";

const ManagePermission = () => {
  const [permissions, setPermissions] = useState({});
  const [data, setData] = useState({});
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState("");
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [newPermission, setNewPermission] = useState({
    category: "",
    subcategory: "",
    view: false,
    edit: false,
    delete: false,
  });

  // Function to fetch data from API
  const token = localStorage.getItem("accessToken");
  const fetchData = async () => {
    try {
      const responsePermissions = await fetch(
        process.env.REACT_APP_API + "admin/roles-with-permissions",
        {
          headers: {
            "X-Access-Token": token,
            "Content-Type": "application/json",
          },
        }
      );
      const permissionsData = await responsePermissions.json();
      setData(permissionsData.data.rows[0].permissions);
      setRoles(permissionsData.data.rows);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("Error fetching data: ", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleModalOpen = () => setModalOpen(true);
  const handleModalClose = () => setModalOpen(false);

  const handleNewPermissionChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewPermission((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleAddNewPermission = async () => {
    const {
      category,
      subcategory,
      view,
      edit,
      delete: deletePerm,
    } = newPermission;

    if (category) {
      const sectionName = subcategory
        ? `${category} - ${subcategory}`
        : category;
      const permissionsArray = [];

      if (view) permissionsArray.push({ sectionName, action: "View" });
      if (edit) permissionsArray.push({ sectionName, action: "Edit" });
      if (deletePerm) permissionsArray.push({ sectionName, action: "Delete" });

      if (permissionsArray.length > 0) {
        try {
          const response = await axios.post(
            process.env.REACT_APP_API + `admin/permissions`,
            permissionsArray,
            {
              headers: {
                "X-Access-Token": token,
                "Content-Type": "application/json",
              },
            }
          );

          if (response.status === 200) {
            handleModalClose();
            Swal.fire({
              title: "Success",
              text: "New permissions added successfully!",
              icon: "success",
              confirmButtonText: "OK",
            }).then(() => {
              setNewPermission({
                category: "",
                subcategory: "",
                view: false,
                edit: false,
                delete: false,
              });
              // Fetch the latest permissions data
              fetchData();
            });
          }
        } catch (error) {
          // Handle errors in the catch block
          console.error("Error adding new permission: ", error);
          Swal.fire({
            title: "Error",
            text: "An error occurred while adding the permission.",
            icon: "error",
            confirmButtonText: "OK",
          });
        }
      }
    } else {
      Swal.fire({
        title: "Error",
        text: "Please fill in both category and subcategory.",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  const initializePermissions = (permissionsData) => {
    const initialState = {};
    for (const category in permissionsData) {
      for (const subCategory in permissionsData[category]) {
        permissionsData[category][subCategory].forEach((permission) => {
          const key = Object.keys(permission)[0];
          initialState[key] = permission[key].assigned;
        });
      }
    }
    setPermissions(initialState);
  };

  const handleCheckboxChange = (key) => {
    setPermissions({
      ...permissions,
      [key]: !permissions[key],
    });
  };

  const handleSelectAllCategory = (category) => {
    const allSelected = Object.values(data[category])
      .flat()
      .every((permission) => permissions[Object.keys(permission)[0]]);
    const newPermissions = { ...permissions };
    Object.values(data[category])
      .flat()
      .forEach((permission) => {
        const key = Object.keys(permission)[0];
        newPermissions[key] = !allSelected;
      });
    setPermissions(newPermissions);
  };

  const handleSelectAll = (subCategory, category) => {
    const allSelected = data[category][subCategory].every(
      (permission) => permissions[Object.keys(permission)[0]]
    );
    const newPermissions = { ...permissions };
    data[category][subCategory].forEach((permission) => {
      const key = Object.keys(permission)[0];
      newPermissions[key] = !allSelected;
    });
    setPermissions(newPermissions);
  };

  const handleRoleChange = (event) => {
    const roleId = event.target.value;
    setSelectedRole(roleId);

    const selectedRoleData = roles.find((role) => role.id === roleId);
    if (selectedRoleData) {
      initializePermissions(selectedRoleData.permissions);
    } else {
      console.warn("Selected role not found in roles array"); // Debug log
    }
  };

  const handleUpdatePermissions = async () => {
    const permissionIds = Object.keys(permissions).filter(
      (key) => permissions[key]
    );

    try {
      await axios.post(
        process.env.REACT_APP_API + `admin/roles/${selectedRole}/permissions`,
        {
          permissionIds,
        },
        {
          headers: {
            "X-Access-Token": token,
            "Content-Type": "application/json",
          },
        }
      );
      Swal.fire({
        title: "Success",
        text: "Permissions updated successfully!",
        icon: "success",
        confirmButtonText: "OK",
      }).then(() => {
        window.location.reload();
      });
    } catch (error) {
      Swal.fire({
        title: "Error",
        timerProgressBar: true,
        text: error.response.data.message,
        icon: "error",
        confirmButtonColor: "#f27474",
        confirmButtonText: "OK",
        dangerMode: true,
        showCloseButton: true,
      });
    }
  };

  // if (loading) return <div>Loading...</div>;

  return (
    <div className="single">
      <Loader isLoading={loading} />
      <Sidebar />
      <div className="singleContainer">
        <Navbar />
        <div className="top">
          <div className="left">
            <div className="permission-title-topSection">
              <h1 className="permission-title">User Permission Management</h1>
            </div>
            <div style={{ display: "flex" }}>
              <Select
                value={selectedRole}
                onChange={handleRoleChange}
                displayEmpty
                className="role-select"
                sx={{ width: 300, height: 50, marginBottom: 5 }}
              >
                <MenuItem value="" disabled>
                  Select Role
                </MenuItem>
                {roles.map((role) => (
                  <MenuItem key={role.id} value={role.id}>
                    {role.role}
                  </MenuItem>
                ))}
              </Select>
            </div>
            <div className="permissions-accordion">
              {Object.keys(data).length > 0 ? (
                Object?.keys(data).map((category) => (
                  <Accordion key={category}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls={`panel${category}-content`}
                      id={`panel${category}-header`}
                    >
                      <Typography variant="h6" className="category-title">
                        {category}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <div className="select-all-container">
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={Object.values(data[category])
                                .flat()
                                .every(
                                  (permission) =>
                                    permissions[Object.keys(permission)[0]]
                                )}
                              onChange={() => handleSelectAllCategory(category)}
                              sx={{ transform: "scale(1.2)" }}
                            />
                          }
                          label={`Select All ${category}`}
                          labelPlacement="end"
                        />
                      </div>
                      {Object?.keys(data[category]).map((subCategory) => (
                        <div key={`${category}-${subCategory}`}>
                          {subCategory !== category && (
                            <Typography
                              variant="subtitle1"
                              className="sub-category-title"
                            >
                              {subCategory}
                            </Typography>
                          )}
                          <div className="permissions-wrapper">
                            <div className="permission-item">
                              <input
                                type="checkbox"
                                id={`select-all-${category}-${subCategory}`}
                                onChange={() =>
                                  handleSelectAll(subCategory, category)
                                }
                                className="checkbox"
                                checked={data[category][subCategory].every(
                                  (permission) =>
                                    permissions[Object.keys(permission)[0]]
                                )}
                              />
                              <label
                                htmlFor={`select-all-${category}-${subCategory}`}
                                className="label"
                              >
                                Select All
                              </label>
                            </div>
                            {data[category][subCategory].map((permission) => {
                              const key = Object.keys(permission)[0];
                              return (
                                <div className="permission-item" key={key}>
                                  <input
                                    type="checkbox"
                                    id={key}
                                    checked={permissions[key] || false}
                                    onChange={() => handleCheckboxChange(key)}
                                    className="checkbox"
                                  />
                                  <label htmlFor={key} className="label">
                                    {permission[key].action}
                                  </label>
                                  {/* Delete Icon outside the accordion */}
                                  {/* <IconButton
                                    aria-label="delete"
                                    onClick={() =>
                                      handleDeletePermission
                                    }
                                    sx={{
                                      position: "absolute",
                                      bottom: 16,
                                      right: 16,
                                      color: "red",
                                      fontSize: "3rem", 
                                    }}
                                  >
                                    <DeleteIcon sx={{ fontSize: "2rem" }} />
                                  </IconButton> */}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </AccordionDetails>
                  </Accordion>
                ))
              ) : (
                <Typography variant="h6" className="category-title">
                  No data available
                </Typography>
              )}
            </div>
            {Object.keys(permissions).length > 0 && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleUpdatePermissions}
                sx={{
                  fontSize: "1rem",
                  padding: "12px 24px",
                  borderRadius: "8px",
                }}
              >
                Update Permissions
              </Button>
            )}
          </div>
        </div>
      </div>
      {/* Modal for Adding New Permission */}
      <Modal open={modalOpen} onClose={handleModalClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 500,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" component="h2" gutterBottom>
            Add New Permission
          </Typography>
          <TextField
            fullWidth
            label="Category"
            name="category"
            value={newPermission.category}
            onChange={handleNewPermissionChange}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Subcategory"
            name="subcategory"
            value={newPermission.subcategory}
            onChange={handleNewPermissionChange}
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                name="view"
                checked={newPermission.view}
                onChange={handleNewPermissionChange}
              />
            }
            label="View"
          />
          <FormControlLabel
            control={
              <Checkbox
                name="edit"
                checked={newPermission.edit}
                onChange={handleNewPermissionChange}
              />
            }
            label="Edit"
          />
          <FormControlLabel
            control={
              <Checkbox
                name="delete"
                checked={newPermission.delete}
                onChange={handleNewPermissionChange}
              />
            }
            label="Delete"
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddNewPermission}
            fullWidth
            sx={{
              mt: 3,
              fontSize: "1rem", // Increase font size
              padding: "12px 24px", // Increase padding for larger button
              borderRadius: "8px",
            }}
          >
            Add Permission
          </Button>
        </Box>
      </Modal>
    </div>
  );
};

export default ManagePermission;
